import React, { useState } from 'react';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import EndpointCard from './components/EndpointCard';
import CodeBlock from './components/CodeBlock';
import SectionNav from './components/SectionNav';
import FeatureGate from '../../components/plan/FeatureGate';

// ─── Data ────────────────────────────────────────────────────────────────────

const NAV_SECTIONS = [
  { id: 'overview', label: 'Overview', icon: 'BookOpen' },
  { id: 'authentication', label: 'Authentication', icon: 'KeyRound' },
  { id: 'forms', label: 'Forms', icon: 'FileText', badge: '6' },
  { id: 'submissions', label: 'Submissions', icon: 'Inbox', badge: '4' },
  { id: 'webhooks', label: 'Webhooks', icon: 'Webhook', badge: '3' },
  { id: 'embed', label: 'Embedding', icon: 'Code2' },
  { id: 'errors', label: 'Errors & Limits', icon: 'AlertTriangle' },
  { id: 'sdks', label: 'SDKs & Examples', icon: 'Package' },
];

const FORMS_ENDPOINTS = [
  {
    method: 'GET',
    path: '/v1/forms',
    summary: 'List all forms',
    description: 'Returns a paginated list of all forms belonging to the authenticated account.',
    params: [
      { name: 'page', type: 'integer', required: false, description: 'Page number (default: 1)' },
      { name: 'per_page', type: 'integer', required: false, description: 'Results per page, max 100 (default: 20)' },
      { name: 'folder_id', type: 'string', required: false, description: 'Filter by folder UUID' },
      { name: 'status', type: 'string', required: false, description: 'Filter by status: active | draft | archived' },
    ],
    response: `{
  "data": [
    {
      "id": "form_01HXYZ123ABC",
      "name": "Contact Us",
      "status": "active",
      "submission_count": 142,
      "created_at": "2026-01-15T10:30:00Z",
      "updated_at": "2026-07-20T08:12:00Z",
      "embed_url": "https://forms.webforms.biz/form_01HXYZ123ABC",
      "folder_id": null
    }
  ],
  "meta": {
    "total": 24,
    "page": 1,
    "per_page": 20,
    "total_pages": 2
  }
}`,
  },
  {
    method: 'GET',
    path: '/v1/forms/:id',
    summary: 'Retrieve a form',
    description: 'Fetches a single form by its unique ID, including all field definitions and settings.',
    params: [
      { name: 'id', type: 'string', required: true, description: 'The form UUID' },
    ],
    response: `{
  "id": "form_01HXYZ123ABC",
  "name": "Contact Us",
  "status": "active",
  "fields": [
    {
      "id": "field_name",
      "type": "text",
      "label": "Full Name",
      "required": true,
      "placeholder": "Enter your name"
    },
    {
      "id": "field_email",
      "type": "email",
      "label": "Email Address",
      "required": true
    }
  ],
  "settings": {
    "redirect_url": "https://example.com/thank-you",
    "notifications_enabled": true,
    "recaptcha_enabled": false
  },
  "created_at": "2026-01-15T10:30:00Z"
}`,
  },
  {
    method: 'POST',
    path: '/v1/forms',
    summary: 'Create a form',
    description: 'Creates a new form with the provided name, fields, and settings.',
    params: [],
    requestBody: `{
  "name": "Newsletter Signup",
  "fields": [
    {
      "type": "email",
      "label": "Email Address",
      "required": true,
      "placeholder": "you@example.com"
    },
    {
      "type": "text",
      "label": "First Name",
      "required": false
    }
  ],
  "settings": {
    "redirect_url": "https://example.com/thanks",
    "notifications_enabled": true
  }
}`,
    response: `{
  "id": "form_01HNEW456DEF",
  "name": "Newsletter Signup",
  "status": "draft",
  "embed_url": "https://forms.webforms.biz/form_01HNEW456DEF",
  "created_at": "2026-07-30T18:00:00Z"
}`,
  },
  {
    method: 'PATCH',
    path: '/v1/forms/:id',
    summary: 'Update a form',
    description: 'Partially updates a form. Only the fields you include in the request body will be changed.',
    params: [
      { name: 'id', type: 'string', required: true, description: 'The form UUID' },
    ],
    requestBody: `{
  "name": "Updated Form Name",
  "status": "active",
  "settings": {
    "redirect_url": "https://example.com/new-thanks"
  }
}`,
    response: `{
  "id": "form_01HXYZ123ABC",
  "name": "Updated Form Name",
  "status": "active",
  "updated_at": "2026-07-30T18:05:00Z"
}`,
  },
  {
    method: 'DELETE',
    path: '/v1/forms/:id',
    summary: 'Delete a form',
    description: 'Permanently deletes a form and all its submissions. This action cannot be undone.',
    params: [
      { name: 'id', type: 'string', required: true, description: 'The form UUID' },
    ],
    response: `{
  "deleted": true,
  "id": "form_01HXYZ123ABC"
}`,
  },
  {
    method: 'GET',
    path: '/v1/forms/:id/embed',
    summary: 'Get embed code',
    description: 'Returns the JavaScript and iFrame embed snippets for a specific form.',
    params: [
      { name: 'id', type: 'string', required: true, description: 'The form UUID' },
      { name: 'type', type: 'string', required: false, description: 'Embed type: js | iframe | react (default: js)' },
    ],
    response: `{
  "js": "<script src=\"https://cdn.webforms.biz/embed.js\"></script>\\n<script>\\n  WebForms.embed({ formId: 'form_01HXYZ123ABC' });\\n</script>",
  "iframe": "<iframe src=\"https://forms.webforms.biz/form_01HXYZ123ABC\" width=\"100%\" height=\"600\" frameborder=\"0\"></iframe>",
  "react": "import { WebForm } from '@webforms/react'
;\\n\\n<WebForm formId=\"form_01HXYZ123ABC\" />"
}`,
  },
];

const SUBMISSIONS_ENDPOINTS = [
  {
    method: 'GET',
    path: '/v1/forms/:id/submissions',
    summary: 'List submissions',
    description: 'Returns all submissions for a specific form, sorted by newest first.',
    params: [
      { name: 'id', type: 'string', required: true, description: 'The form UUID' },
      { name: 'page', type: 'integer', required: false, description: 'Page number (default: 1)' },
      { name: 'per_page', type: 'integer', required: false, description: 'Results per page, max 100' },
      { name: 'from', type: 'string', required: false, description: 'ISO 8601 start date filter' },
      { name: 'to', type: 'string', required: false, description: 'ISO 8601 end date filter' },
    ],
    response: `{
  "data": [
    {
      "id": "sub_01HABC789GHI",
      "form_id": "form_01HXYZ123ABC",
      "data": {
        "field_name": "Jane Smith",
        "field_email": "jane@example.com",
        "field_message": "Hello, I'd like more info."
      },
      "metadata": {
        "ip": "203.0.113.42",
        "user_agent": "Mozilla/5.0...",
        "referrer": "https://example.com/contact"
      },
      "submitted_at": "2026-07-29T14:22:00Z"
    }
  ],
  "meta": { "total": 142, "page": 1, "per_page": 20 }
}`,
  },
  {
    method: 'GET',
    path: '/v1/submissions/:id',
    summary: 'Retrieve a submission',
    description: 'Fetches a single submission by its ID with full field data and metadata.',
    params: [
      { name: 'id', type: 'string', required: true, description: 'The submission UUID' },
    ],
    response: `{
  "id": "sub_01HABC789GHI",
  "form_id": "form_01HXYZ123ABC",
  "data": {
    "field_name": "Jane Smith",
    "field_email": "jane@example.com"
  },
  "metadata": {
    "ip": "203.0.113.42",
    "referrer": "https://example.com"
  },
  "submitted_at": "2026-07-29T14:22:00Z"
}`,
  },
  {
    method: 'DELETE',
    path: '/v1/submissions/:id',
    summary: 'Delete a submission',
    description: 'Permanently deletes a single submission record.',
    params: [
      { name: 'id', type: 'string', required: true, description: 'The submission UUID' },
    ],
    response: `{
  "deleted": true,
  "id": "sub_01HABC789GHI"
}`,
  },
  {
    method: 'POST',
    path: '/v1/forms/:id/submissions/export',
    summary: 'Export submissions',
    description: 'Triggers an async export of all submissions. Returns a download URL when ready.',
    params: [
      { name: 'id', type: 'string', required: true, description: 'The form UUID' },
    ],
    requestBody: `{
  "format": "csv",
  "date_range": {
    "from": "2026-01-01T00:00:00Z",
    "to": "2026-07-30T23:59:59Z"
  },
  "fields": ["field_name", "field_email"]
}`,
    response: `{
  "export_id": "exp_01HEXP999JKL",
  "status": "processing",
  "download_url": null,
  "estimated_seconds": 12
}`,
  },
];

const WEBHOOKS_ENDPOINTS = [
  {
    method: 'GET',
    path: '/v1/webhooks',
    summary: 'List webhooks',
    description: 'Returns all webhook endpoints configured for the account.',
    params: [],
    response: `{
  "data": [
    {
      "id": "wh_01HWBK321MNO",
      "url": "https://example.com/webhooks/webforms",
      "events": ["submission.created", "form.updated"],
      "active": true,
      "secret": "whsec_••••••••••••••••",
      "created_at": "2026-03-10T09:00:00Z"
    }
  ]
}`,
  },
  {
    method: 'POST',
    path: '/v1/webhooks',
    summary: 'Create a webhook',
    description: 'Registers a new webhook endpoint to receive real-time event notifications.',
    params: [],
    requestBody: `{
  "url": "https://example.com/webhooks/webforms",
  "events": [
    "submission.created",
    "submission.deleted",
    "form.created",
    "form.updated",
    "form.deleted"
  ],
  "active": true
}`,
    response: `{
  "id": "wh_01HWBK321MNO",
  "url": "https://example.com/webhooks/webforms",
  "events": ["submission.created"],
  "active": true,
  "secret": "whsec_a1b2c3d4e5f6g7h8i9j0",
  "created_at": "2026-07-30T18:00:00Z"
}`,
  },
  {
    method: 'DELETE',
    path: '/v1/webhooks/:id',
    summary: 'Delete a webhook',
    description: 'Removes a webhook endpoint. No further events will be delivered to this URL.',
    params: [
      { name: 'id', type: 'string', required: true, description: 'The webhook UUID' },
    ],
    response: `{
  "deleted": true,
  "id": "wh_01HWBK321MNO"
}`,
  },
];

// ─── Section Components ───────────────────────────────────────────────────────

const OverviewSection = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-2xl font-heading font-bold text-foreground mb-3">API Reference</h2>
      <p className="text-muted-foreground leading-relaxed max-w-2xl">
        The WebForms REST API lets you programmatically create and manage forms, retrieve submissions,
        configure webhooks, and generate embed codes. All responses are JSON.
      </p>
    </div>

    {/* Base URL */}
    <div className="p-4 rounded-lg bg-muted/40 border border-border">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Base URL</p>
      <code className="font-mono text-sm text-primary">https://api.webforms.biz</code>
    </div>

    {/* Quick stats */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[
        { label: 'API Version', value: 'v1', icon: 'Tag' },
        { label: 'Protocol', value: 'HTTPS', icon: 'Lock' },
        { label: 'Format', value: 'JSON', icon: 'Braces' },
        { label: 'Auth', value: 'Bearer', icon: 'KeyRound' },
      ]?.map((item) => (
        <div key={item?.label} className="p-4 rounded-lg border border-border bg-card flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name={item?.icon} size={18} color="var(--color-primary)" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{item?.label}</p>
            <p className="text-sm font-semibold text-foreground font-mono">{item?.value}</p>
          </div>
        </div>
      ))}
    </div>

    {/* Versioning note */}
    <div className="flex gap-3 p-4 rounded-lg bg-blue-500/8 border border-blue-500/20">
      <Icon name="Info" size={18} color="#60a5fa" className="flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-medium text-blue-400 mb-1">Versioning</p>
        <p className="text-sm text-muted-foreground">
          The API version is included in the URL path (<code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">/v1/</code>).
          Breaking changes will be released under a new version. We maintain backward compatibility for at least 12 months after deprecation notices.
        </p>
      </div>
    </div>
  </div>
);

const AuthSection = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-2xl font-heading font-bold text-foreground mb-3">Authentication</h2>
      <p className="text-muted-foreground leading-relaxed max-w-2xl">
        WebForms uses API keys to authenticate requests. You can manage your API keys from
        <strong className="text-foreground"> Account Settings → API Keys</strong>.
      </p>
    </div>

    {/* Key types */}
    <div className="grid md:grid-cols-2 gap-4">
      {[
        {
          title: 'Secret Key',
          prefix: 'wf_sk_',
          icon: 'KeyRound',
          color: 'border-amber-500/30 bg-amber-500/5',
          iconColor: '#f59e0b',
          description: 'Full read/write access. Keep this server-side only. Never expose in client-side code or public repos.',
        },
        {
          title: 'Publishable Key',
          prefix: 'wf_pk_',
          icon: 'Globe',
          color: 'border-emerald-500/30 bg-emerald-500/5',
          iconColor: '#10b981',
          description: 'Read-only access for embedding forms. Safe to include in client-side JavaScript and HTML.',
        },
      ]?.map((key) => (
        <div key={key?.title} className={`p-5 rounded-lg border ${key?.color}`}>
          <div className="flex items-center gap-3 mb-3">
            <Icon name={key?.icon} size={20} color={key?.iconColor} />
            <h3 className="font-semibold text-foreground">{key?.title}</h3>
          </div>
          <code className="text-xs font-mono text-muted-foreground block mb-3">{key?.prefix}••••••••••••••••</code>
          <p className="text-sm text-muted-foreground">{key?.description}</p>
        </div>
      ))}
    </div>

    {/* Usage */}
    <div>
      <h3 className="text-base font-semibold text-foreground mb-4">Using Your API Key</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Pass your secret key as a Bearer token in the <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">Authorization</code> header:
      </p>
      <CodeBlock
        language="bash"
        label="cURL"
        code={`curl https://api.webforms.biz/v1/forms \\
  -H "Authorization: Bearer wf_sk_live_your_secret_key_here" \ -H"Content-Type: application/json"`}
      />
    </div>

    {/* Security tips */}
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-foreground">Security Best Practices</h3>
      {[
        'Store secret keys in environment variables, never in source code.',
        'Rotate keys immediately if you suspect they have been compromised.',
        'Use separate keys for development and production environments.',
        'Restrict key permissions to only the scopes your integration needs.',
      ]?.map((tip, i) => (
        <div key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
          <Icon name="ShieldCheck" size={15} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
          <span>{tip}</span>
        </div>
      ))}
    </div>
  </div>
);

const FormsSection = () => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-heading font-bold text-foreground mb-3">Forms</h2>
      <p className="text-muted-foreground leading-relaxed max-w-2xl">
        Create, read, update, and delete forms. Each form has a unique ID used across all API calls.
      </p>
    </div>
    <div className="space-y-3">
      {FORMS_ENDPOINTS?.map((ep, i) => <EndpointCard key={i} endpoint={ep} />)}
    </div>
  </div>
);

const SubmissionsSection = () => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-heading font-bold text-foreground mb-3">Submissions</h2>
      <p className="text-muted-foreground leading-relaxed max-w-2xl">
        Access and manage form submissions. Submissions are immutable once created — they can only be read or deleted.
      </p>
    </div>
    <div className="space-y-3">
      {SUBMISSIONS_ENDPOINTS?.map((ep, i) => <EndpointCard key={i} endpoint={ep} />)}
    </div>
  </div>
);

const WebhooksSection = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-2xl font-heading font-bold text-foreground mb-3">Webhooks</h2>
      <p className="text-muted-foreground leading-relaxed max-w-2xl">
        Webhooks push real-time event notifications to your server whenever something happens in WebForms.
        Register an HTTPS endpoint and we'll POST a JSON payload to it.
      </p>
    </div>

    {/* Events table */}
    <div>
      <h3 className="text-base font-semibold text-foreground mb-4">Available Events</h3>
      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Event</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Description</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['submission.created', 'A new form submission was received'],
              ['submission.deleted', 'A submission was permanently deleted'],
              ['form.created', 'A new form was created'],
              ['form.updated', 'A form was modified'],
              ['form.deleted', 'A form was deleted'],
              ['form.published', 'A form status changed to active'],
              ['form.archived', 'A form was archived'],
            ]?.map(([event, desc]) => (
              <tr key={event} className="border-b border-border last:border-0">
                <td className="px-4 py-3 font-mono text-xs text-primary">{event}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Payload example */}
    <div>
      <h3 className="text-base font-semibold text-foreground mb-4">Payload Example</h3>
      <CodeBlock
        language="json"
        label="submission.created payload"
        code={`{
  "id": "evt_01HEVT555PQR",
  "type": "submission.created",
  "created_at": "2026-07-30T18:02:01Z",
  "data": {
    "submission": {
      "id": "sub_01HABC789GHI",
      "form_id": "form_01HXYZ123ABC",
      "data": {
        "field_name": "Jane Smith",
        "field_email": "jane@example.com"
      },
      "submitted_at": "2026-07-30T18:02:00Z"
    }
  }
}`}
      />
    </div>

    {/* Signature verification */}
    <div>
      <h3 className="text-base font-semibold text-foreground mb-3">Signature Verification</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Every webhook request includes a <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">WebForms-Signature</code> header.
        Verify it to ensure the payload came from WebForms:
      </p>
      <CodeBlock
        language="javascript"
        label="Node.js verification"
        code={`const crypto = require('crypto');

function verifyWebhook(payload, signature, secret) {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(payload, 'utf8')
    .digest('hex');

  const sig = signature.replace('sha256=', '');
  return crypto.timingSafeEqual(
    Buffer.from(expected, 'hex'),
    Buffer.from(sig, 'hex')
  );
}

// In your Express route:
app.post('/webhooks/webforms', express.raw({ type: 'application/json' }), (req, res) => {
  const sig = req.headers['webforms-signature'];
  const isValid = verifyWebhook(req.body, sig, process.env.WEBHOOK_SECRET);

  if (!isValid) return res.status(401).send('Invalid signature');

  const event = JSON.parse(req.body);
  console.log('Event received:', event.type);
  res.json({ received: true });
});`}
      />
    </div>

    {/* Endpoints */}
    <div className="space-y-3">
      {WEBHOOKS_ENDPOINTS?.map((ep, i) => <EndpointCard key={i} endpoint={ep} />)}
    </div>
  </div>
);

const EmbedSection = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-2xl font-heading font-bold text-foreground mb-3">Embedding Forms</h2>
      <p className="text-muted-foreground leading-relaxed max-w-2xl">
        Embed WebForms into any website or app using JavaScript, iFrame, or the React component.
        All methods support auto-resize, theming, and event callbacks.
      </p>
    </div>

    {/* JS embed */}
    <div>
      <h3 className="text-base font-semibold text-foreground mb-2">JavaScript Embed (Recommended)</h3>
      <p className="text-sm text-muted-foreground mb-4">Supports auto-resize, CSS isolation, and event callbacks.</p>
      <CodeBlock
        language="html"
        label="HTML"
        code={`<!-- 1. Add the loader script once per page -->
<script src="https://cdn.webforms.biz/embed.js" async></script>

<!-- 2. Place the container where you want the form -->
<div id="my-form"></div>

<!-- 3. Initialize -->
<script>
  window.addEventListener('WebFormsReady', () => {
    WebForms.embed({
      formId: 'form_01HXYZ123ABC',
      container: '#my-form',
      autoResize: true,
      theme: {
        primaryColor: '#107570',
        borderRadius: '8px'
      },
      onLoad: () => console.log('Form loaded'),
      onSubmit: (data) => {
        console.log('Submitted:', data);
        // redirect, show thank-you, etc.
      },
      onError: (err) => console.error('Error:', err)
    });
  });
</script>`}
      />
    </div>

    {/* iFrame */}
    <div>
      <h3 className="text-base font-semibold text-foreground mb-2">iFrame Embed</h3>
      <p className="text-sm text-muted-foreground mb-4">Simplest option — no JavaScript required.</p>
      <CodeBlock
        language="html"
        label="HTML"
        code={`<iframe
  src="https://forms.webforms.biz/form_01HXYZ123ABC"
  width="100%"
  height="600"
  frameborder="0"
  allow="payment"
  style="border: none; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.08);"
  title="Contact Form">
</iframe>`}
      />
    </div>

    {/* React */}
    <div>
      <h3 className="text-base font-semibold text-foreground mb-2">React Component</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Install the official React package for a first-class component experience.
      </p>
      <CodeBlock
        language="bash"
        label="Terminal"
        code={`npm install @webforms/react`}
      />
      <div className="mt-3">
        <CodeBlock
          language="javascript"
          label="React"
          code={`import { WebForm } from '@webforms/react';

export default function ContactPage() {
  return (
    <WebForm
      formId="form_01HXYZ123ABC"
      publishableKey="wf_pk_live_your_key_here"
      theme={{ primaryColor: '#107570' }}
      onSubmit={(data) => {
        console.log('Form data:', data);
      }}
    />
  );
}`}
        />
      </div>
    </div>

    {/* URL params */}
    <div>
      <h3 className="text-base font-semibold text-foreground mb-4">Pre-filling Fields via URL</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Pass field values as query parameters to pre-fill form fields:
      </p>
      <CodeBlock
        language="html"
        label="URL"
        code={`https://forms.webforms.biz/form_01HXYZ123ABC?field_email=user@example.com&field_name=Jane`}
      />
    </div>
  </div>
);

const ErrorsSection = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-2xl font-heading font-bold text-foreground mb-3">Errors & Rate Limits</h2>
      <p className="text-muted-foreground leading-relaxed max-w-2xl">
        WebForms uses standard HTTP status codes. Errors include a machine-readable <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">code</code> and
        a human-readable <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">message</code>.
      </p>
    </div>

    {/* Error format */}
    <div>
      <h3 className="text-base font-semibold text-foreground mb-4">Error Response Format</h3>
      <CodeBlock
        language="json"
        label="Error response"
        code={`{
  "error": {
    "code": "form_not_found",
    "message": "No form with ID form_01HXYZ123ABC exists in your account.",
    "status": 404,
    "request_id": "req_01HREQ999STU"
  }
}`}
      />
    </div>

    {/* Status codes */}
    <div>
      <h3 className="text-base font-semibold text-foreground mb-4">HTTP Status Codes</h3>
      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground w-24">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Meaning</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['200', 'OK — Request succeeded'],
              ['201', 'Created — Resource was created'],
              ['204', 'No Content — Successful delete'],
              ['400', 'Bad Request — Invalid parameters or request body'],
              ['401', 'Unauthorized — Missing or invalid API key'],
              ['403', 'Forbidden — Valid key but insufficient permissions'],
              ['404', 'Not Found — Resource does not exist'],
              ['409', 'Conflict — Resource already exists'],
              ['422', 'Unprocessable Entity — Validation failed'],
              ['429', 'Too Many Requests — Rate limit exceeded'],
              ['500', 'Internal Server Error — Something went wrong on our end'],
            ]?.map(([code, desc]) => (
              <tr key={code} className="border-b border-border last:border-0">
                <td className={`px-4 py-3 font-mono text-xs font-bold ${
                  code?.startsWith('2') ? 'text-emerald-400' :
                  code?.startsWith('4') ? 'text-amber-400' : 'text-red-400'
                }`}>{code}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Rate limits */}
    <div>
      <h3 className="text-base font-semibold text-foreground mb-4">Rate Limits</h3>
      <div className="rounded-lg border border-border overflow-hidden mb-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Plan</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Requests / minute</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Requests / day</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Starter', '60', '10,000'],
              ['Professional', '300', '100,000'],
              ['Enterprise', '1,000', 'Unlimited'],
            ]?.map(([plan, rpm, rpd]) => (
              <tr key={plan} className="border-b border-border last:border-0">
                <td className="px-4 py-3 text-xs font-medium text-foreground">{plan}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground font-mono">{rpm}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground font-mono">{rpd}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-sm text-muted-foreground">
        Rate limit headers are included in every response:
        <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded mx-1">X-RateLimit-Limit</code>,
        <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded mx-1">X-RateLimit-Remaining</code>,
        <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded mx-1">X-RateLimit-Reset</code>.
      </p>
    </div>
  </div>
);

const SDKsSection = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-2xl font-heading font-bold text-foreground mb-3">SDKs & Code Examples</h2>
      <p className="text-muted-foreground leading-relaxed max-w-2xl">
        Official client libraries and full integration examples to get you up and running quickly.
      </p>
    </div>

    {/* SDK cards */}
    <div className="grid md:grid-cols-3 gap-4">
      {[
        { lang: 'Node.js', icon: 'Package', pkg: 'npm install @webforms/node', status: 'Stable', color: 'text-green-400' },
        { lang: 'Python', icon: 'Package', pkg: 'pip install webforms-python', status: 'Stable', color: 'text-green-400' },
        { lang: 'PHP', icon: 'Package', pkg: 'composer require webforms/php', status: 'Beta', color: 'text-amber-400' },
      ]?.map((sdk) => (
        <div key={sdk?.lang} className="p-4 rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Icon name={sdk?.icon} size={18} color="var(--color-primary)" />
              <span className="font-semibold text-sm text-foreground">{sdk?.lang}</span>
            </div>
            <span className={`text-xs font-medium ${sdk?.color}`}>{sdk?.status}</span>
          </div>
          <code className="text-xs font-mono text-muted-foreground block bg-muted/50 px-3 py-2 rounded">{sdk?.pkg}</code>
        </div>
      ))}
    </div>

    {/* Node.js example */}
    <div>
      <h3 className="text-base font-semibold text-foreground mb-4">Node.js — Full Integration Example</h3>
      <CodeBlock
        language="javascript"
        label="Node.js"
        code={`const WebForms = require('@webforms/node');

const client = new WebForms({
  apiKey: process.env.WEBFORMS_SECRET_KEY,
});

// List all active forms
const { data: forms } = await client.forms.list({ status: 'active' });
console.log(\`Found \${forms.length} active forms\`);

// Get submissions for a specific form
const { data: submissions } = await client.forms.submissions.list(
  'form_01HXYZ123ABC',
  { per_page: 50 }
);

// Register a webhook
const webhook = await client.webhooks.create({
  url: 'https://yourapp.com/webhooks/webforms',
  events: ['submission.created'],
});
console.log('Webhook secret:', webhook.secret);`}
      />
    </div>

    {/* Python example */}
    <div>
      <h3 className="text-base font-semibold text-foreground mb-4">Python — Full Integration Example</h3>
      <CodeBlock
        language="python"
        label="Python"
        code={`import webforms
import os

client = webforms.Client(api_key=os.environ['WEBFORMS_SECRET_KEY'])

# List forms
forms = client.forms.list(status='active')
print(f"Found {len(forms.data)} active forms")

# Fetch submissions
submissions = client.forms.submissions.list(
    form_id='form_01HXYZ123ABC',
    per_page=50
)
for sub in submissions.data:
    print(sub.data['field_email'])

# Create a webhook
webhook = client.webhooks.create(
    url='https://yourapp.com/webhooks/webforms',
    events=['submission.created', 'form.updated']
)
print('Webhook ID:', webhook.id)`}
      />
    </div>

    {/* Next.js API route */}
    <div>
      <h3 className="text-base font-semibold text-foreground mb-4">Next.js — Webhook Handler</h3>
      <CodeBlock
        language="javascript"
        label="pages/api/webhooks/webforms.js"
        code={`import { verifyWebhookSignature } from '@webforms/node';

export const config = { api: { bodyParser: false } };

async function buffer(readable) {
  const chunks = [];
  for await (const chunk of readable) chunks.push(chunk);
  return Buffer.concat(chunks);
}

export default async function handler(req, res) {
  if (req.method !== 'POST') return res.status(405).end();

  const buf = await buffer(req);
  const sig = req.headers['webforms-signature'];

  let event;
  try {
    event = verifyWebhookSignature(buf, sig, process.env.WEBHOOK_SECRET);
  } catch (err) {
    return res.status(400).json({ error: err.message });
  }

  switch (event.type) {
    case 'submission.created':
      await handleNewSubmission(event.data.submission);
      break;
    default:
      console.log('Unhandled event:', event.type);
  }

  res.json({ received: true });
}`}
      />
    </div>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const SECTION_MAP = {
  overview: <OverviewSection />,
  authentication: <AuthSection />,
  forms: <FormsSection />,
  submissions: <SubmissionsSection />,
  webhooks: <WebhooksSection />,
  embed: <EmbedSection />,
  errors: <ErrorsSection />,
  sdks: <SDKsSection />,
};

const ApiReference = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar stats={{}} />
      <FeatureGate feature="api_access">
      <div className="pt-16 flex">
        {/* Left sidebar — desktop */}
        <aside className="hidden lg:flex flex-col fixed top-16 left-64 bottom-0 w-56 border-r border-border bg-card z-10 overflow-y-auto">
          <div className="p-4">
            <div className="flex items-center gap-2 mb-5 px-1">
              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="BookOpen" size={15} color="var(--color-primary)" />
              </div>
              <span className="text-sm font-semibold text-foreground">API Reference</span>
            </div>
            <SectionNav
              sections={NAV_SECTIONS}
              activeSection={activeSection}
              onSelect={setActiveSection}
            />
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 lg:ml-[calc(256px+224px)] min-w-0">
          {/* Mobile nav toggle */}
          <div className="lg:hidden sticky top-16 z-10 bg-card border-b border-border px-4 py-3 flex items-center gap-3">
            <button
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-smooth"
            >
              <Icon name="Menu" size={18} />
              <span>API Sections</span>
              <Icon name={mobileNavOpen ? 'ChevronUp' : 'ChevronDown'} size={14} />
            </button>
            <span className="text-muted-foreground/40">·</span>
            <span className="text-sm font-medium text-foreground capitalize">{activeSection}</span>
          </div>

          {/* Mobile nav dropdown */}
          {mobileNavOpen && (
            <div className="lg:hidden bg-card border-b border-border px-4 py-3">
              <SectionNav
                sections={NAV_SECTIONS}
                activeSection={activeSection}
                onSelect={(id) => { setActiveSection(id); setMobileNavOpen(false); }}
              />
            </div>
          )}

          {/* Section content */}
          <div className="max-w-4xl mx-auto px-6 py-10">
            {SECTION_MAP?.[activeSection]}
          </div>
        </main>
      </div>
      </FeatureGate>
    </div>
  );
};

export default ApiReference;
